import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { activeAssessments } from '../features/assessments/registry';
import AssessmentCard from '../features/assessments/AssessmentCard';
import AptitudeCard from '../components/assessment/cards/AptitudeCard';
import CognitiveCard from '../components/assessment/cards/CognitiveCard';
import SkillCard from '../components/assessment/cards/SkillCard';
import MegaCard from '../components/assessment/cards/MegaCard';
import { apiClient } from '../api/client';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

const ASSESSMENT_DESCRIPTIONS = {
  aptitude: 'Evaluate reasoning ability, numerical aptitude, and problem-solving skills.',
  cognitive: 'Assess cognitive thinking, pattern recognition, and logical reasoning.',
  skill: 'Measure role-specific skills and technical competencies.',
  mega: 'Comprehensive assessment covering aptitude, cognitive thinking, situational handling, and coding.',
  extended: 'In-depth extended assessment across multiple competency domains.',
  edutech: 'Evaluate teaching competency across pedagogical foundations and classroom management.',
  salesforce: 'Role-specific WorkScore for field sales execution, objection handling, and strategy under pressure.',
  customercare: 'Role-specific WorkScore for customer communication, service recovery, and retention judgment.',
  callingsales: 'Role-specific WorkScore for outbound calling performance, objections, and conversion consistency.',
  feetonstreet: 'Role-specific WorkScore for field promotion, adaptability, and territory execution behaviors.',
  bigfive_productivity: 'Assess personality traits and productivity strengths in one combined evaluation.'
};

const ASSESSMENT_COLORS = {
  aptitude: 'amber',
  cognitive: 'purple',
  skill: 'emerald',
  mega: 'amber',
  extended: 'indigo',
  edutech: 'indigo',
  salesforce: 'blue',
  customercare: 'green',
  callingsales: 'amber',
  feetonstreet: 'indigo',
  bigfive_productivity: 'blue'
};

const AssessmentStartPage = () => {
  const isInviteFlow = useMemo(() => Boolean(sessionStorage.getItem('assessment_ref')), []);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(isInviteFlow);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isInviteFlow) {
      return;
    }

    let mounted = true;

    const loadAssignment = async () => {
      try {
        const refToken = sessionStorage.getItem('assessment_ref');
        const response = await apiClient.get('/assessment/my-assignment', {
          params: refToken ? { ref: refToken } : undefined
        });

        if (!mounted) return;

        if (response.data?.found) {
          setAssignment(response.data);
          setError(null);
        } else {
          setAssignment(null);
          setError(response.data?.reason || 'No valid assignment found');
        }
      } catch (fetchError) {
        if (!mounted) return;
        setAssignment(null);
        setError('Unable to load your assessment. Please try again.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    // Invite flow is assignment-driven to avoid showing unrelated static modules.
    loadAssignment();

    return () => {
      mounted = false;
    };
  }, [isInviteFlow]);

  const inviteAssessmentCard = assignment?.found
    ? {
        key: assignment.assessment_key,
        label: assignment.assessment_label,
        subtitle: 'Assigned assessment',
        description: ASSESSMENT_DESCRIPTIONS[assignment.assessment_key] || 'Complete your assigned assessment.',
        route: `/assessment/${assignment.assessment_key}/verify`,
        color: ASSESSMENT_COLORS[assignment.assessment_key] || 'indigo'
      }
    : null;

  return (
    <div className="assessment-hub-shell min-h-screen">
      <div className="floating-orb orb-a" />
      <div className="floating-orb orb-b" />
      <div className="floating-orb orb-c" />

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl w-full mx-auto text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3 tracking-tight">
            CogniQ Assessment Hub
          </h1>
          <p className="text-base md:text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            Secure assessment workflows with proctoring, structured scoring, and enterprise-grade reporting.
          </p>

          {isInviteFlow ? (
            loading ? (
              <div className="glass-elevated p-8 rounded-2xl max-w-2xl mx-auto">
                <LoadingSpinner size="lg" className="mb-3" />
                <p className="text-sm text-slate-600">Loading your assigned assessment...</p>
              </div>
            ) : error ? (
              <div className="glass-elevated p-8 rounded-2xl max-w-2xl mx-auto">
                <h2 className="text-2xl font-semibold text-slate-900">Assessment Unavailable</h2>
                <p className="text-sm text-slate-600 mt-3">{error}</p>
                <p className="text-sm text-slate-500 mt-2">Please contact your administrator.</p>
              </div>
            ) : (
              <div className="grid gap-6 max-w-2xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45 }}
                >
                  <AssessmentCard
                    assessment={{
                      ...inviteAssessmentCard,
                      route: `/assessment/${assignment.assessment_key}/verify`
                    }}
                  />
                </motion.div>
              </div>
            )
          ) : activeAssessments.length > 0 ? (
            <div className="grid gap-6 max-w-2xl mx-auto">
              {activeAssessments.map((assessment, index) => (
                <motion.div
                  key={assessment.key}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.12 * index }}
                >
                  {assessment.key === 'aptitude' ? (
                    <AptitudeCard />
                  ) : assessment.key === 'cognitive' ? (
                    <CognitiveCard />
                  ) : assessment.key === 'skill' ? (
                    <SkillCard />
                  ) : assessment.key === 'mega' ? (
                    <MegaCard />
                  ) : (
                    <AssessmentCard assessment={assessment} />
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="glass-elevated p-6 rounded-2xl max-w-2xl mx-auto">
              <h2 className="text-xl font-semibold text-slate-900">No Active Assessments</h2>
              <p className="text-sm text-slate-600 mt-2">
                All assessments are currently disabled. Please contact your administrator.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AssessmentStartPage;
